// components/CockpitTitleSwitcher.tsx — S1c (decision eb659c8d, Huan
// 2026-10-12; spec COCKPIT-ASSISTANT-SPEC.md §S1c; brief
// /home/ubuntu/cockpit-build/S1C-NAV-BRIEF.md). Build to mockup-v10's own
// menu (Main.dc.html / Folded.dc.html / Calendar.dc.html, plates 01-03 —
// read directly: the Design appifact's own SKILL.md instructs against
// rendering a .dc.html canvas through Playwright/a browser, and no
// interactive-browser tool was available in this build's environment
// either, so the mockup's exact inline markup/styles were the design
// reference rather than a rendered screenshot — reported as a judgement
// call).
//
// REPLACES the personal pack's SideRail/NavZone (no global rail, no
// bottom tab bar — App.tsx mounts this instead, for isPersonal only).
// Every other pack's SideRail/NavZone/MoreSheet usage is untouched.
//
// Same scrim/box/role="dialog"/Escape construction as MoreSheet (the
// settled shape for "something rose over the app and will go away
// again") — not a new surface archetype. One panel design serves both
// the desktop dropdown AND the folded/phone "full-width sheet under the
// title": mockup-v10's own Folded.dc.html draws the folded menu the SAME
// way (a top-anchored panel, left/right 12px margins, NOT a bottom
// sheet) — see CockpitTitleSwitcher.css for the width split.
import { useEffect, useRef, useState } from 'preact/hooks';
import { cockpitSurfaces, type CockpitSurface } from '../lib/cockpitSurfaces';
import type { PackVertical } from '../routes/route';
import './CockpitTitleSwitcher.css';

const USAGE_KEY = 'cockpit.switcher.usage.v1';
const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? navigator.userAgent ?? '');

function readUsage(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(USAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    // Private browsing, a blocked store, a corrupt value — most-used-first
    // just degrades to declared order, never an error.
    return {};
  }
}

function bumpUsage(key: string): void {
  try {
    const usage = readUsage();
    usage[key] = (usage[key] ?? 0) + 1;
    window.localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
  } catch {
    // Nothing to do: the switcher still navigates, it just won't learn.
  }
}

/** Most-used-first WITHIN each group — the mockup's three groups (and
 * Settings alone, last) are fixed structure, not a usage-ordered list;
 * only the order of items INSIDE a group moves. Ties keep the surface's
 * own declared order (a stable sort on the original index). */
function orderByUsage(surfaces: CockpitSurface[], usage: Record<string, number>): CockpitSurface[] {
  return surfaces
    .map((s, i) => ({ s, i, n: usage[s.key] ?? 0 }))
    .sort((a, b) => b.n - a.n || a.i - b.i)
    .map((x) => x.s);
}

function groupSurfaces(surfaces: CockpitSurface[], usage: Record<string, number>): CockpitSurface[][] {
  const byGroup = new Map<string, CockpitSurface[]>();
  for (const s of surfaces) byGroup.set(s.group, [...(byGroup.get(s.group) ?? []), s]);
  const order: CockpitSurface['group'][] = ['primary', 'surfaces', 'settings'];
  return order.filter((g) => byGroup.has(g)).map((g) => orderByUsage(byGroup.get(g) ?? [], usage));
}

export interface CockpitTitleSwitcherProps {
  pack: PackVertical;
  /** The current screen's own name (routes/route.ts's routeLabel) — shown
   *  on the title itself even when the screen isn't one of the switcher's
   *  own doors (a thread detail, a calendar event, …). */
  title: string;
  /** Which switcher surface (cockpitSurfaces key) the current screen
   *  belongs to, for highlighting — undefined when the current screen
   *  maps to none (its own door still works; nothing in the menu lights
   *  up). */
  activeKey?: string | undefined;
  /** Surface key -> a live count, from the SAME numbers the rail badges
   *  showed before this chunk (App.tsx) — never invented here. */
  counts?: Partial<Record<string, number>>;
  /** Bare routePath()-shaped path ("/mail", not "#/mail") — the switcher
   *  prepends the "#" itself, same as navHref does for the rail. */
  onNavigate: (path: string) => void;
}

export function CockpitTitleSwitcher({ pack, title, activeKey, counts, onNavigate }: CockpitTitleSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState<Record<string, number>>(() => readUsage());
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // atlas-review (2026-10-15, 4d026db5): a hardcoded CSS `top` for the
  // folded/phone panel covered the title chip, because it guessed the
  // title's own y-position rather than reading it — wrong the moment
  // anything (the phone topbar, a safe-area inset) sits above the title.
  // MEASURED instead: the real button's own `getBoundingClientRect()`,
  // `position: fixed` off THAT, so the panel sits right under the title on
  // every layout above it, phone and rail alike — no guessing twice.
  const [menuStyle, setMenuStyle] = useState<Record<string, string>>({});

  const surfaces = cockpitSurfaces(pack);
  // The attention dot: ANOTHER surface (not the one we're standing on)
  // carries a non-zero count. activeKey undefined (a detail screen with
  // no switcher door of its own) means every counted surface is "another
  // one," which is the honest reading — nothing here is the one you're on.
  const dot = surfaces.some((s) => s.key !== activeKey && (counts?.[s.key] ?? 0) > 0);

  function go(surface: CockpitSurface) {
    bumpUsage(surface.key);
    setUsage(readUsage());
    setOpen(false);
    onNavigate(surface.route);
  }

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function onPointerDown(e: PointerEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node) && e.target !== buttonRef.current) {
        setOpen(false);
      }
    }
    function place() {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const top = `${Math.round(rect.bottom + 4)}px`;
      // Narrow = the folded/phone sheet: full width under the title, same
      // 12px margins mockup-v10's own Folded.dc.html draws. Wide = the
      // rail's 248px dropdown, left-aligned on the title button.
      setMenuStyle(
        window.innerWidth <= 640
          ? { position: 'fixed', top, left: '12px', right: '12px', width: 'auto' }
          : { position: 'fixed', top, left: `${Math.round(rect.left)}px`, width: '248px' }
      );
    }
    place();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', place);
    const first = boxRef.current?.querySelector('a,button');
    if (first instanceof HTMLElement) first.focus();
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // ⌘1…⌘9 / Ctrl+1…9 — fixed to the surface (cockpitSurfaces.ts), not to a
  // reorderable menu position. Lives for as long as this switcher is
  // mounted, i.e. for as long as the personal pack is booted.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > 9) return;
      const surface = surfaces.find((s) => s.shortcut === n);
      if (!surface) return;
      e.preventDefault();
      go(surface);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack]);

  // H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2) — ⌘0/Ctrl+0, the laptop's own
  // "go home" from EVERY cockpit screen. A separate listener from ⌘1…9
  // above (not just `n === 0` added to it) because this one alone must
  // stay silent while the person is typing — the Atlas column's own text
  // box included (the brief's own words) — a guard the fixed-digit
  // shortcuts above have never needed (⌘ + a digit is not a native text-
  // editing chord in the way ⌘0 already is browser-side, zoom reset).
  // Routes through the SAME `go()` a menu tap uses (bump usage, close an
  // open menu, navigate) rather than a second ad hoc path to '/home'.
  useEffect(() => {
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return true;
      // `isContentEditable` is read first (the real browser signal, also
      // true for a DESCENDANT of a contenteditable root); the attribute
      // check beside it is a fallback some test DOMs (happy-dom) don't
      // compute `isContentEditable` from at all.
      return target.isContentEditable || target.getAttribute('contenteditable') === 'true';
    }
    function onKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (e.key !== '0') return;
      if (isTypingTarget(e.target)) return;
      const home = surfaces.find((s) => s.key === 'home');
      if (!home) return;
      e.preventDefault();
      go(home);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack]);

  const groups = groupSurfaces(surfaces, usage);

  return (
    <div class="cts">
      <button
        type="button"
        ref={buttonRef}
        class="cts__title"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span class="cts__label">{title}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" class="cts__chev">
          <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {dot ? <span class="cts__dot" aria-label="Something elsewhere needs you" /> : null}
      </button>
      {open ? (
        <div class="cts__menu" ref={boxRef} role="menu" aria-label="Go to" style={menuStyle}>
          {groups.map((group, gi) => (
            <div class="cts__group" key={group[0]?.key ?? gi}>
              {gi > 0 ? <div class="cts__divider" aria-hidden="true" /> : null}
              {group.map((surface) => {
                const count = counts?.[surface.key] ?? 0;
                return (
                  <button
                    type="button"
                    key={surface.key}
                    role="menuitem"
                    class={`cts__item${surface.key === activeKey ? ' cts__item--active' : ''}`}
                    aria-current={surface.key === activeKey ? 'page' : undefined}
                    onClick={() => go(surface)}
                  >
                    <span class="cts__name">{surface.label}</span>
                    {count > 0 ? <span class="cts__count">{count > 99 ? '99+' : count}</span> : null}
                    {surface.shortcut ? (
                      <span class="cts__shortcut">{MAC ? `⌘${surface.shortcut}` : `Ctrl+${surface.shortcut}`}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
